import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Eye, FileDown, Mail, Sparkles } from "lucide-react";
import { errMessage } from "../../lib/errors";
import { menuHighlights } from "../../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin › Menu — print "Today's special" insert cards (4 per A4, crop marks). */
export default function SpecialSlipPanel({ authHeaders, toast }) {
  const [specialId, setSpecialId] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [dishId, setDishId] = useState("");
  const [count, setCount] = useState(4);
  const [extras, setExtras] = useState([]);

  useEffect(() => {
    axios.get(`${API}/menu/special`).then(({ data }) => {
      const id = data?.item?.id ?? null;
      setSpecialId(id);
      setDishId((prev) => prev || (id != null ? String(id) : ""));
    }).catch(() => {});
    axios.get(`${API}/menu`).then(({ data }) => {
      const staticIds = new Set(menuHighlights.map((m) => m.id));
      setExtras((data.items || []).filter((o) => !staticIds.has(o.id) && o.name));
    }).catch(() => {});
  }, []);

  const options = useMemo(() => [
    ...menuHighlights.map((m) => ({ id: m.id, name: m.name })),
    ...extras.map((o) => ({ id: o.id, name: o.name })),
  ], [extras]);

  const url = dishId ? `${API}/special-slip.pdf?dish_id=${dishId}&count=${count}` : null;
  const isSpecial = specialId != null && String(specialId) === dishId;
  const dishName = options.find((o) => String(o.id) === dishId)?.name || "Special";

  const emailNow = async () => {
    if (!dishId) return;
    setEmailing(true);
    try {
      const { data } = await axios.post(`${API}/admin/special-slip/email`, { dish_id: Number(dishId) }, { headers: authHeaders });
      toast?.({
        title: data.status === "sent" ? "Slips emailed" : "Email failed",
        description: data.status === "sent" ? `${data.dish} slips sent to ${data.recipient}.` : "Check the Resend key / owner alert email in Settings.",
      });
    } catch (e) {
      toast?.({ title: "Email failed", description: errMessage(e) });
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4" data-testid="special-slip-panel">
      <div className="flex-1">
        <div className="section-label mb-2 flex items-center gap-2">
          <Sparkles size={13} /> Today's Special Slips
        </div>
        <p className="text-sm text-[var(--mm-cream-muted)]">
          Cream A6 insert cards — photo, name, price, description and a small order QR — four per A4 sheet with crop marks. Tuck one into each physical menu.
        </p>
        <p className="text-xs text-[var(--mm-cream-muted)] opacity-70 mt-1" data-testid="special-slip-hint">
          {specialId == null
            ? "No dish is flagged as Today's Special yet — pick any dish below, or set one with the Sparkles button in the menu list."
            : isSpecial ? "Pre-selected: the dish currently flagged as Today's Special." : "Override: printing a dish other than the current special."}
        </p>
        <p className="text-xs text-[var(--mm-cream-muted)] opacity-70 mt-1" data-testid="special-slip-auto-note">
          Auto-email: the slips land in the owner inbox at 7:00am whenever the special changes (Settings › Special Slip Email Enabled).
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={dishId}
          onChange={(e) => setDishId(e.target.value)}
          data-testid="special-slip-dish"
          className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1.5 text-xs focus:border-[var(--mm-accent)] outline-none max-w-[220px]"
        >
          <option value="" className="bg-[#111]">Choose a dish…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id} className="bg-[#111]">
              {`${o.name}${o.id === specialId ? " · today's special" : ""}`}
            </option>
          ))}
        </select>
        <select
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          data-testid="special-slip-count"
          className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1.5 text-xs focus:border-[var(--mm-accent)] outline-none"
        >
          {[4, 8, 12, 20].map((n) => <option key={n} value={n} className="bg-[#111]">{`${n} cards`}</option>)}
        </select>
        <a
          href={url || undefined}
          target="_blank"
          rel="noreferrer"
          data-testid="special-slip-preview"
          aria-disabled={!url}
          className={`btn-outline inline-flex items-center gap-2 text-xs ${url ? "" : "opacity-40 pointer-events-none"}`}
        >
          <Eye size={13} /> Preview
        </a>
        <a
          href={url || undefined}
          download={`Mamamina-Special-${dishName.replace(/[^\w-]+/g, "-")}.pdf`}
          data-testid="special-slip-download"
          aria-disabled={!url}
          className={`btn-primary inline-flex items-center gap-2 ${url ? "" : "opacity-40 pointer-events-none"}`}
        >
          <FileDown size={14} /> Download PDF
        </a>
        <button
          type="button"
          onClick={emailNow}
          disabled={!url || emailing}
          data-testid="special-slip-email"
          className="btn-outline inline-flex items-center gap-2 text-xs disabled:opacity-40"
        >
          <Mail size={13} /> {emailing ? "Sending…" : "Email me now"}
        </button>
      </div>
    </div>
  );
}
